import { STATUS_WORDS } from '../lib/format.js';
import type { CheckStatus } from '../lib/types.js';
import { cx } from './classes.js';

/**
 * The status vocabulary (design v1): four states, one drawn glyph each,
 * always accompanied by the word somewhere in the row — colour alone is
 * never the signal, and there is no pill. `sm` is the 22px row glyph; `lg`
 * the 44px round one on the issued screen.
 */
export type GlyphSize = 'sm' | 'lg';

const BG: Record<CheckStatus, string> = {
  pass: 'bg-good-bg text-good',
  attention: 'bg-warn-bg text-warn',
  fail: 'bg-crit-bg text-crit',
  unknown: 'bg-sunk text-mute',
};

const SIZE: Record<GlyphSize, string> = {
  sm: 'size-[22px] rounded-glyph',
  lg: 'size-11 rounded-full',
};

function Shape({ status, px }: { status: CheckStatus; px: number }) {
  const common = {
    width: px,
    height: px,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 3,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  switch (status) {
    case 'pass':
      return (
        <svg {...common}>
          <path d="M5 12l5 5L20 7" />
        </svg>
      );
    case 'attention':
      return (
        <svg {...common}>
          <path d="M12 5v9" />
          <path d="M12 18.5v.5" />
        </svg>
      );
    case 'fail':
      return (
        <svg {...common}>
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      );
    case 'unknown':
      return (
        <span className="text-[13px] font-semibold leading-none" aria-hidden="true">
          ?
        </span>
      );
  }
}

export function StatusGlyph({
  status,
  size = 'sm',
  label,
  className,
}: {
  status: CheckStatus;
  size?: GlyphSize;
  /** Accessible name; defaults to the status word. */
  label?: string;
  className?: string;
}) {
  return (
    <span
      role="img"
      aria-label={label ?? STATUS_WORDS[status]}
      data-status={status}
      className={cx(
        'inline-flex shrink-0 items-center justify-center',
        SIZE[size],
        BG[status],
        className,
      )}
    >
      <Shape status={status} px={size === 'lg' ? 20 : 12} />
    </span>
  );
}

/** Text colour for a status word next to a glyph. */
export const STATUS_TEXT: Record<CheckStatus, string> = {
  pass: 'text-good',
  attention: 'text-warn',
  fail: 'text-crit',
  unknown: 'text-mute',
};
